import React, { useState, useEffect } from 'react';
import { Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Flex,
  VStack,
  Link as ChakraLink,
  Icon,
  Spacer,
  Button,
  Text,
  Avatar,
  Divider,
  IconButton,
  useDisclosure,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerBody,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import {
  FiLogOut, FiServer, FiBox, FiActivity, FiKey,
  FiClipboard, FiGlobe, FiHeart, FiGrid, FiLayers,
  FiExternalLink, FiCpu, FiDatabase, FiBook, FiUsers,
  FiHardDrive, FiClock, FiBell, FiMenu, FiRepeat, FiDollarSign, FiShield,
} from 'react-icons/fi';

import ClusterOverview from './components/ClusterOverview';
import Pods from './components/Pods';
import Deployments from './components/Deployments';
import Services from './components/Services';
import Nodes from './components/Nodes';
import PersistentVolumes from './components/PersistentVolumes';
import Ingresses from './components/Ingresses';
import Events from './components/Events';
import CronJobs from './components/CronJobs';
import HealthEndpoints from './components/HealthEndpoints';
import ApiKeys from './components/ApiKeys';
import AuditLog from './components/AuditLog';
import TrafficStatus from './components/TrafficStatus';
import TrafficActivity from './components/TrafficActivity';
import TrafficHealth from './components/TrafficHealth';
import PostgresStatus from './components/PostgresStatus';
import SuperAdmin from './components/SuperAdmin';
import BackupConfig from './components/BackupConfig';
import BackupHistory from './components/BackupHistory';
import BackupWebhooks from './components/BackupWebhooks';
import Costs from './components/Costs';
import ServiceCredentials from './components/ServiceCredentials';
import LoginForm from './components/Login';

const TRAFFIC_MANAGER_URL = 'https://dashboard.selwynevents.com';

const NavButton = ({ to, icon, children, isActive }) => (
  <ChakraLink as={RouterLink} to={to} _hover={{ textDecoration: 'none' }}>
    <Button
      leftIcon={<Icon as={icon} boxSize={5} />}
      width="full"
      justifyContent="flex-start"
      variant={isActive ? 'solid' : 'ghost'}
      colorScheme={isActive ? 'teal' : 'whiteAlpha'}
      color={isActive ? 'white' : 'whiteAlpha.800'}
      bg={isActive ? 'whiteAlpha.200' : 'transparent'}
      _hover={{
        bg: isActive ? 'whiteAlpha.300' : 'whiteAlpha.100',
        color: 'white',
      }}
      borderRadius="lg"
      py={6}
      fontSize="md"
      fontWeight={isActive ? '600' : '400'}
    >
      {children}
    </Button>
  </ChakraLink>
);

const ExternalNavButton = ({ href, icon, children }) => (
  <ChakraLink href={href} isExternal _hover={{ textDecoration: 'none' }}>
    <Button
      leftIcon={<Icon as={icon} boxSize={5} />}
      rightIcon={<Icon as={FiExternalLink} boxSize={3} />}
      width="full"
      justifyContent="flex-start"
      variant="ghost"
      colorScheme="whiteAlpha"
      color="whiteAlpha.800"
      bg="transparent"
      _hover={{
        bg: 'whiteAlpha.100',
        color: 'white',
      }}
      borderRadius="lg"
      py={6}
      fontSize="md"
      fontWeight="400"
    >
      {children}
    </Button>
  </ChakraLink>
);

function App() {
  const [token, setToken] = useState(localStorage.getItem('token'));
  const navigate = useNavigate();
  const location = useLocation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  useEffect(() => { onClose(); }, [location.pathname, onClose]);

  // The AWS pages (Costs, Service Credentials) only exist when the infra manager runs on AWS,
  // and only for super admins.
  const [showCosts, setShowCosts] = useState(false);
  useEffect(() => {
    if (!token) return undefined;
    let active = true;
    fetch('/api/v1/billing/status/', { headers: { Authorization: `Token ${token}` } })
      .then((res) => (res.ok ? res.json() : null))
      .then((status) => { if (active) setShowCosts(Boolean(status?.available && status?.can_view)); })
      .catch(() => { if (active) setShowCosts(false); });
    return () => { active = false; };
  }, [token]);

  const handleLogin = () => {
    setToken(localStorage.getItem('token'));
    navigate('/');
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setToken(null);
  };

  if (!token) {
    return <LoginForm onLogin={handleLogin} />;
  }

  const renderSidebarContent = () => (
    <>
      {/* Logo/Brand */}
      <Flex align="center" px={4} mb={6}>
        <Box w={12} h={12} borderRadius="full" overflow="hidden" bg="white" p={0.5}>
          <img src="/static/selwyn-logo.png" alt="Selwyn" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
        </Box>
        <Box ml={3}>
          <Text fontSize="lg" fontWeight="bold" color="white">Infra Manager</Text>
        </Box>
      </Flex>

      <Divider borderColor="whiteAlpha.300" mb={4} />

      {/* Navigation */}
      <Text px={4} fontSize="xs" color="whiteAlpha.600" fontWeight="semibold" textTransform="uppercase" mb={2}>
        Cluster
      </Text>
      <NavButton to="/" icon={FiGrid} isActive={location.pathname === '/'}>
        Overview
      </NavButton>
      <NavButton to="/pods" icon={FiBox} isActive={location.pathname === '/pods'}>
        Pods
      </NavButton>
      <NavButton to="/deployments" icon={FiLayers} isActive={location.pathname === '/deployments'}>
        Deployments
      </NavButton>
      <NavButton to="/services" icon={FiServer} isActive={location.pathname === '/services'}>
        Services
      </NavButton>
      <NavButton to="/nodes" icon={FiCpu} isActive={location.pathname === '/nodes'}>
        Nodes
      </NavButton>
      <NavButton to="/volumes" icon={FiHardDrive} isActive={location.pathname === '/volumes'}>
        Persistent Volumes
      </NavButton>
      <NavButton to="/ingresses" icon={FiGlobe} isActive={location.pathname === '/ingresses'}>
        Ingresses
      </NavButton>
      <NavButton to="/events" icon={FiActivity} isActive={location.pathname === '/events'}>
        Events
      </NavButton>
      <NavButton to="/cronjobs" icon={FiRepeat} isActive={location.pathname === '/cronjobs'}>
        CronJobs
      </NavButton>

      <Text px={4} fontSize="xs" color="whiteAlpha.600" fontWeight="semibold" textTransform="uppercase" mt={6} mb={2}>
        Monitoring
      </Text>
      <NavButton to="/health" icon={FiHeart} isActive={location.pathname === '/health'}>
        Health Endpoints
      </NavButton>

      {showCosts && (
        <>
          <Text px={4} fontSize="xs" color="whiteAlpha.600" fontWeight="semibold" textTransform="uppercase" mt={6} mb={2}>
            AWS
          </Text>
          <NavButton to="/costs" icon={FiDollarSign} isActive={location.pathname === '/costs'}>
            Costs
          </NavButton>
          <NavButton to="/service-credentials" icon={FiShield} isActive={location.pathname === '/service-credentials'}>
            Service Credentials
          </NavButton>
        </>
      )}

      <Text px={4} fontSize="xs" color="whiteAlpha.600" fontWeight="semibold" textTransform="uppercase" mt={6} mb={2}>
        Traffic Manager
      </Text>
      <NavButton to="/traffic/status" icon={FiActivity} isActive={location.pathname === '/traffic/status'}>
        Status
      </NavButton>
      <NavButton to="/traffic/activity" icon={FiClipboard} isActive={location.pathname === '/traffic/activity'}>
        Activity
      </NavButton>
      <NavButton to="/traffic/health" icon={FiHeart} isActive={location.pathname === '/traffic/health'}>
        Service Health
      </NavButton>

      <Text px={4} fontSize="xs" color="whiteAlpha.600" fontWeight="semibold" textTransform="uppercase" mt={6} mb={2}>
        Database
      </Text>
      <NavButton to="/postgres" icon={FiDatabase} isActive={location.pathname === '/postgres'}>
        PostgreSQL
      </NavButton>

      <Text px={4} fontSize="xs" color="whiteAlpha.600" fontWeight="semibold" textTransform="uppercase" mt={6} mb={2}>
        Backups
      </Text>
      <NavButton to="/backups" icon={FiClock} isActive={location.pathname === '/backups'}>
        History &amp; Status
      </NavButton>
      <NavButton to="/backups/config" icon={FiHardDrive} isActive={location.pathname === '/backups/config'}>
        Configuration
      </NavButton>
      <NavButton to="/backups/webhooks" icon={FiBell} isActive={location.pathname === '/backups/webhooks'}>
        Webhooks
      </NavButton>

      <Text px={4} fontSize="xs" color="whiteAlpha.600" fontWeight="semibold" textTransform="uppercase" mt={6} mb={2}>
        Super Admin
      </Text>
      <NavButton to="/super-admin" icon={FiUsers} isActive={location.pathname === '/super-admin'}>
        Manage Users
      </NavButton>

      <Text px={4} fontSize="xs" color="whiteAlpha.600" fontWeight="semibold" textTransform="uppercase" mt={6} mb={2}>
        Administration
      </Text>
      <NavButton to="/api-keys" icon={FiKey} isActive={location.pathname === '/api-keys'}>
        API Keys
      </NavButton>
      <NavButton to="/audit-log" icon={FiClipboard} isActive={location.pathname === '/audit-log'}>
        Audit Log
      </NavButton>

      <Text px={4} fontSize="xs" color="whiteAlpha.600" fontWeight="semibold" textTransform="uppercase" mt={6} mb={2}>
        API Docs
      </Text>
      <ExternalNavButton href="/api/docs/swagger/" icon={FiBook}>
        Swagger UI
      </ExternalNavButton>
      <ExternalNavButton href="/api/docs/redoc/" icon={FiBook}>
        ReDoc
      </ExternalNavButton>

      <Text px={4} fontSize="xs" color="whiteAlpha.600" fontWeight="semibold" textTransform="uppercase" mt={6} mb={2}>
        External
      </Text>
      <ExternalNavButton href={TRAFFIC_MANAGER_URL} icon={FiGlobe}>
        Traffic Manager
      </ExternalNavButton>

      <Spacer />

      {/* User Section */}
      <Divider borderColor="whiteAlpha.300" />
      <Flex align="center" px={4} py={3}>
        <Avatar size="sm" bg="teal.400" />
        <Box ml={3} flex={1}>
          <Text fontSize="sm" color="white" fontWeight="medium">Admin User</Text>
          <Text fontSize="xs" color="whiteAlpha.600">Logged in</Text>
        </Box>
      </Flex>
      <Button
        leftIcon={<Icon as={FiLogOut} />}
        onClick={handleLogout}
        width="full"
        justifyContent="flex-start"
        variant="ghost"
        color="whiteAlpha.800"
        _hover={{ bg: 'whiteAlpha.100', color: 'white' }}
        borderRadius="lg"
      >
        Logout
      </Button>
    </>
  );

  return (
    <Flex direction="column" height="100vh" bg="gray.50">
      {/* Mobile top bar */}
      <Flex
        display={{ base: 'flex', md: 'none' }}
        flexShrink={0}
        height="56px"
        bg="gray.800"
        align="center"
        px={4}
        justify="space-between"
        boxShadow="md"
      >
        <IconButton
          icon={<Icon as={FiMenu} />}
          onClick={onOpen}
          variant="ghost"
          color="white"
          aria-label="Open navigation"
          _hover={{ bg: 'whiteAlpha.200' }}
        />
        <Text color="white" fontWeight="bold" fontSize="md">Infra Manager</Text>
        <IconButton
          icon={<Icon as={FiLogOut} />}
          onClick={handleLogout}
          variant="ghost"
          color="white"
          aria-label="Logout"
          _hover={{ bg: 'whiteAlpha.200' }}
        />
      </Flex>

      {/* Desktop layout row */}
      <Flex flex="1" overflow="hidden">
        {/* Desktop Sidebar */}
        <VStack
          as="nav"
          display={{ base: 'none', md: 'flex' }}
          width="280px"
          py={6}
          px={4}
          bgGradient="linear(to-b, gray.700, gray.900)"
          spacing={2}
          align="stretch"
          boxShadow="xl"
          overflowY="auto"
          flexShrink={0}
        >
          {renderSidebarContent()}
        </VStack>

        {/* Main Content */}
        <Box flex="1" p={{ base: 4, md: 8 }} overflowY="auto">
          <Routes>
            <Route path="/" element={<ClusterOverview />} />
            <Route path="/pods" element={<Pods />} />
            <Route path="/deployments" element={<Deployments />} />
            <Route path="/services" element={<Services />} />
            <Route path="/nodes" element={<Nodes />} />
            <Route path="/volumes" element={<PersistentVolumes />} />
            <Route path="/ingresses" element={<Ingresses />} />
            <Route path="/events" element={<Events />} />
            <Route path="/cronjobs" element={<CronJobs />} />
            <Route path="/health" element={<HealthEndpoints />} />
            <Route path="/costs" element={<Costs />} />
            <Route path="/service-credentials" element={<ServiceCredentials />} />
            <Route path="/traffic/status" element={<TrafficStatus />} />
            <Route path="/traffic/activity" element={<TrafficActivity />} />
            <Route path="/traffic/health" element={<TrafficHealth />} />
            <Route path="/postgres" element={<PostgresStatus />} />
            <Route path="/backups" element={<BackupHistory />} />
            <Route path="/backups/config" element={<BackupConfig />} />
            <Route path="/backups/webhooks" element={<BackupWebhooks />} />
            <Route path="/super-admin" element={<SuperAdmin />} />
            <Route path="/api-keys" element={<ApiKeys />} />
            <Route path="/audit-log" element={<AuditLog />} />
          </Routes>
        </Box>
      </Flex>

      {/* Mobile nav drawer */}
      <Drawer isOpen={isOpen} onClose={onClose} placement="left">
        <DrawerOverlay />
        <DrawerContent bgGradient="linear(to-b, gray.700, gray.900)" maxW="280px">
          <DrawerCloseButton color="white" top={4} />
          <DrawerBody p={0}>
            <VStack spacing={2} align="stretch" py={6} px={4} minH="100%">
              {renderSidebarContent()}
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Flex>
  );
}

export default App;
